import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ArrowRight, FileSpreadsheet, PackagePlus } from 'lucide-react';
import {
  CloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  PageContainer,
} from '../../Styles/AppStyles';
import { Header } from '../../Components/Header/Header';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import { COLORS } from '../../Styles/Colors';
import { AddFormData, AddItemForm, BulkImportComponent, submitNonAbatixItem } from './NonAbatixItemForms';

const INVENTORY_LINK = '/inventory?type=non-abatix';

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ icon, title, description, actionLabel, onAction }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      p: 3,
      borderRadius: 2,
      border: '1px solid #e3e6ea',
      backgroundColor: COLORS.white,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${COLORS.teal}14`,
        color: COLORS.teal,
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#2b2f33' }}>{title}</Typography>
    <Typography sx={{ color: COLORS.gray, fontSize: '0.9rem', lineHeight: 1.6, flex: 1 }}>{description}</Typography>
    <Button
      variant="contained"
      onClick={onAction}
      sx={{
        alignSelf: 'flex-start',
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: COLORS.teal,
        boxShadow: 'none',
        '&:hover': { backgroundColor: COLORS.teal, boxShadow: 'none', opacity: 0.92 },
      }}
    >
      {actionLabel}
    </Button>
  </Box>
);

/**
 * Non-ABATIX → Add / Import Items.
 * Non-ABATIX items are now listed on the shared Inventory page; adding new
 * ones (one at a time or from Excel) lives here in the Non-ABATIX menu.
 */
const NonAbatixAddItemsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const handleAddItem = async (formData: AddFormData) => {
    try {
      await submitNonAbatixItem(formData);
      showSnackbar('Item successfully added to inventory', 'success');
      setLastResult(`${formData.item_id} was added to inventory.`);
      setShowAddModal(false);
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to add item', 'error');
      throw err;
    }
  };

  return (
    <PageContainer>
      <Header
        title="Add / Import Items"
        subtitle="Add Non-ABATIX items to a location's inventory. They appear on Inventory → View Inventory alongside ABATIX items."
      />

      <Box sx={{ px: { xs: 1.25, md: 3 }, pb: 3, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 960 }}>
        {lastResult && (
          <Box
            role="status"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: `1px solid ${COLORS.success}55`,
              backgroundColor: `${COLORS.success}10`,
            }}
          >
            <Typography sx={{ fontSize: '0.9rem', color: '#1f6b31' }}>{lastResult}</Typography>
            <Button
              size="small"
              endIcon={<ArrowRight size={16} />}
              onClick={() => navigate(INVENTORY_LINK)}
              sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.teal }}
            >
              View Non-ABATIX inventory
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <ActionCard
            icon={<PackagePlus size={22} />}
            title="Add a single item"
            description="Enter one item's part number, category, price, stock levels and an optional image."
            actionLabel="Add Item"
            onAction={() => setShowAddModal(true)}
          />
          <ActionCard
            icon={<FileSpreadsheet size={22} />}
            title="Bulk import from Excel"
            description="Download the Excel template, fill in one row per item, then upload it to add many items at once."
            actionLabel="Bulk Import"
            onAction={() => setShowImportModal(true)}
          />
        </Box>
      </Box>

      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            <ModalHeader>Add Inventory Item</ModalHeader>
            <AddItemForm
              ovs={ovs || []}
              onSubmit={handleAddItem}
              onCancel={() => setShowAddModal(false)}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <BulkImportComponent
              onClose={() => setShowImportModal(false)}
              onSuccess={() => setLastResult('Your import finished. The new items are in inventory.')}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default NonAbatixAddItemsPage;
